import * as yup from 'yup';
import { DocumentNode } from 'graphql';
import { useForm } from 'react-form-state-manager';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { GetSessionQuery } from '../Auth/SessionProvider';
import { assetUrl, getServerErrors, guessCountry, guessTimezone } from '../../common/helpers';
import AddProjectMutation from './AddProjectMutation';
import GetLocalizationOptionsQuery from './GetLocalizationOptionsQuery';
import SessionStorage from '../../common/SessionStorage';
import UI from '../../common/UI';
import useCountries from '../../common/useCountries';
import useLocale from '../../common/useLocale';
import useMutation from '../../api/useMutation';
import useQuery from '../../api/useQuery';
import useScroll from '../../common/useScroll';
import useUrlState from '../../common/useUrlState';
import useValidation from '../../common/useValidation';

const addProjectSchema = yup.object().shape({
  organisation_name: yup.string().required(),
  organisation_country: yup.string().ensure(),
});

interface AddProjectPageProps {
  refetchQueries?: DocumentNode[];
}

const AddProjectPage = ({ refetchQueries = [GetSessionQuery] }: AddProjectPageProps) => {
  const { t } = useTranslation();
  const { locale, timezone: defaultTimezone } = useLocale();
  const history = useHistory();
  const { scrollTo } = useScroll();
  const { countries } = useCountries();

  const timezone = defaultTimezone || guessTimezone();

  const { data: localization } = useQuery(GetLocalizationOptionsQuery);

  const form = useForm({
    values: {
      organisation_name: '',
      organisation_country: guessCountry(),
    },
  });

  const [urlState] = useUrlState({
    parse: (params, { string }) => ({
      ...params,
      promo: string(params.promo),
    }),
  });

  // Make sure that the timezone given by the browser is also accepted by the server.
  const timezoneExists = localization
    ? localization.timezones.filter(({ name }) => name === timezone).length > 0
    : true;

  const [addProject, { error, loading }] = useMutation(
    AddProjectMutation,
    {
      variables: {
        input: {
          locale,
          timezone: timezoneExists ? timezone : 'Europe/Amsterdam',
          promo: urlState.promo,
          tracking_data: JSON.stringify({
            referrer: SessionStorage.getItem('referrer'),
            originalLocation: SessionStorage.getItem('originalLocation'),
          }),
          ...form.values,
        },
      },
      refetchQueries,
      awaitRefetchQueries: refetchQueries.length > 0,
      onCompleted: (result) => {
        history.push(`/${result.addProject.id}/events/add?new=true`);
      },
      onError: () => {
        scrollTo('AddProjectForm');
      },
    },
  );

  const { valid, errors } = useValidation({
    schema: addProjectSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Section sc={{ compact: true }}>
      <UI.GridContainer>
        <UI.Div sc={{ textAlign: 'center' }}>
          <img src={assetUrl('images/rocket.svg')} alt="" height={120} />
          <UI.H1 sc={{ my: 1 }}>
            {t('welcome_to_atleta')}
          </UI.H1>
          <UI.Paragraph sc={{ big: true, mb: 0 }} style={{ textWrap: 'balance' }}>
            {t('add_project_intro')}
          </UI.Paragraph>
        </UI.Div>

        <UI.Card sc={{ spacing: 'lg' }}>
          <UI.Form onSubmit={() => addProject()}>
            <UI.FormGrid>
              <UI.ServerErrors error={error} />

              <UI.InputGroup sc={{ valid: !errors.organisation_name, touched: form.touched.organisation_name }}>
                <UI.InputLabel htmlFor="OrganisationName">
                  {t('organisation_name')}
                  {' '}
                  <UI.RequiredMark />
                </UI.InputLabel>
                <UI.DebouncedInput
                  id="OrganisationName"
                  {...form.text('organisation_name')}
                  sc={{ size: 'lg' }}
                  maxLength={50}
                />
                <UI.ErrorMessages
                  attribute={t('organisation_name')}
                  errors={errors.organisation_name}
                />
              </UI.InputGroup>

              <UI.InputGroup sc={{ valid: !errors.organisation_country, touched: form.touched.organisation_country }}>
                <UI.InputLabel htmlFor="OrganisationCountry">
                  {t('country')}
                  {' '}
                  <UI.RequiredMark />
                </UI.InputLabel>
                <UI.Select
                  {...form.select('organisation_country', [null, ...countries])}
                  id="OrganisationCountry"
                  sc={{ size: 'lg' }}
                >
                  <option value={0}> </option>
                  {countries.map((country, index) => (
                    <option {...form.option(index + 1)} key={index + 1}>{t(`common:countries.${country}`)}</option>
                  ))}
                </UI.Select>
                <UI.ErrorMessages attribute={t('country')} errors={errors.organisation_country} />
              </UI.InputGroup>

              <UI.Button
                type="submit"
                sc={{ brand: 'secondary', size: 'lg', loading, width: ['100%', 'auto'] }}
                disabled={!form.changed() || loading || !valid}
              >
                {t('next_create_an_event')}
                {' '}
                {loading && <UI.Loader sc={{ size: 12, brand: 'secondary.200', ml: 1 }} />}
              </UI.Button>
            </UI.FormGrid>
          </UI.Form>
        </UI.Card>
      </UI.GridContainer>
    </UI.Section>
  );
};

export default AddProjectPage;
